import { useTranslation } from "react-i18next";

import { AlertModal } from "@/components/ui";
import { sanitizeUserFacingErrorText } from "@/lib/errorMessages";

import { ReceiptConfirmModal } from "./ReceiptConfirmModal";
import { ReceiptManualAmountModal } from "./ReceiptManualAmountModal";
import { ReceiptOcrFailureModal } from "./ReceiptOcrFailureModal";
import type { ReceiptFlow } from "./useReceiptFlow";

interface ReceiptFlowModalsProps {
  flow: ReceiptFlow;
}

/** Modals driven by `useReceiptFlow` (confirm, OCR failure, manual total, error alert). */
export function ReceiptFlowModals({ flow }: ReceiptFlowModalsProps) {
  const { t: tCommon } = useTranslation("common");
  const { t } = useTranslation("bookings");
  const { receiptConfirm, receiptOcrFailure, manualAmountEntry, receiptAlert } =
    flow;

  return (
    <>
      {receiptConfirm ? (
        <ReceiptConfirmModal
          analyzeResult={receiptConfirm.analyzeResult}
          confirmingReceipt={flow.confirmingReceipt}
          onConfirm={() => void flow.confirmReceipt()}
          onRetake={() => void flow.rejectReceipt()}
        />
      ) : null}

      {receiptOcrFailure ? (
        <ReceiptOcrFailureModal
          message={receiptOcrFailure.message}
          manualAmountRequired={receiptOcrFailure.manualAmountRequired}
          remainingValidationAttempts={
            receiptOcrFailure.remainingValidationAttempts
          }
          maxValidationAttempts={receiptOcrFailure.maxValidationAttempts}
          isProcessing={
            flow.processingByBookingId[receiptOcrFailure.booking.id] ?? false
          }
          onFileRetry={(file) =>
            void flow.uploadReceipt(receiptOcrFailure.booking, file)
          }
          onManualAmount={flow.switchOcrFailureToManualAmount}
        />
      ) : null}

      {manualAmountEntry ? (
        <ReceiptManualAmountModal
          isSubmitting={flow.submittingManualAmount}
          onSubmit={(totalCents) => void flow.submitManualAmount(totalCents)}
          onClose={flow.closeManualAmount}
        />
      ) : null}

      {receiptAlert ? (
        <AlertModal
          closeLabel={tCommon("close")}
          isOpen
          onClose={flow.closeAlert}
          title={receiptAlert.title}
          message={
            sanitizeUserFacingErrorText(
              receiptAlert.message,
              tCommon("somethingWentWrong"),
            ) ?? tCommon("somethingWentWrong")
          }
          variant="error"
          buttonText={t("alertGotIt")}
        />
      ) : null}
    </>
  );
}
